import axios from "axios";

// Build a Response-shaped error object for failures where `fetch()`
// itself rejected (network down, DNS, CORS, offline, aborted) rather
// than returning an HTTP error status.
//
// Why this exists: on a non-2xx HTTP status `fetchData` throws the raw
// `Response`, which has a `.json()` method. Saga catch blocks across the
// app universally do `const error = yield err.json()` BEFORE dispatching
// the `*_ERROR` action that clears their loading flag. But when `fetch()`
// rejects at the transport layer it throws a `TypeError` with NO
// `.json()` — so that line throws *inside the catch*, the `yield put(
// ...Error())` never runs, the reducer's error case never fires, and the
// page is stranded on a permanent spinner. (This is the real root cause
// behind the "stuck loading forever" reports — it only triggers on
// transport failures, not server 4xx/5xx, which is why it looked
// intermittent.)
//
// Normalizing here means every existing `err.json()` call site keeps
// working unchanged AND the loading flag always clears, without editing
// ~100 saga catch blocks individually.
function makeNetworkErrorResponse(networkErr) {
  // The browser's own text ("Failed to fetch (api-host)", "Load failed",
  // "NetworkError when attempting…") reached the sign-in form verbatim.
  // Show one plain sentence; keep the raw one for debugging.
  const detail = (networkErr && networkErr.message) || "";
  if (detail) console.warn("Network request failed:", detail);
  const message =
    "Couldn't reach Nubod. Check your connection and try again.";
  return {
    // Sagas read `.json()`; some also read `.status`. 0 is the
    // conventional "no HTTP response" status (matches XHR semantics).
    status: 0,
    ok: false,
    networkError: true,
    json: async () => ({ message, networkError: true }),
    text: async () => message,
  };
}

export const fetchData = async (url, config, body) => {
  let response;
  try {
    response = await fetch(url, config, body);
  } catch (networkErr) {
    throw makeNetworkErrorResponse(networkErr);
  }
  if (response.status >= 200 && response.status < 300) {
    return response.text().then(text => {
      try {
        //if response is not json, will throw and error and return text
        const data = JSON.parse(text);
        return data;
      } catch (err) {
        return text;
      }
    })
    // const data = await response.isJson() ? response.json() : response;
    // return data;
  } else {
    throw response;
  }
};

// `fetchAPI(path, config, body)` —
//   - `path`   relative to `/api`
//   - `config` standard fetch init (method, etc.)
//   - `body`   optional JSON-serializable payload. Stringified into
//              `config.body` and a `Content-Type: application/json`
//              header is added unless the caller already set one.
//
// The previous version of this function passed `body` as a 3rd arg to
// `fetchData` -> `fetch(url, config, body)`, but `fetch()` only takes two
// arguments — the body was silently dropped, breaking POST/PATCH/PUT
// callers that didn't manually `body: JSON.stringify(...)` themselves.
export const fetchAPI = (path, config, body) => {
  const baseHeaders = {
    "x-auth-token": window.localStorage.getItem("token"),
    ...(config || {}).headers,
  };
  const finalConfig = { ...config, headers: baseHeaders };
  if (body !== undefined && body !== null) {
    finalConfig.body =
      typeof body === "string" ? body : JSON.stringify(body);
    if (!baseHeaders["Content-Type"] && !baseHeaders["content-type"]) {
      baseHeaders["Content-Type"] = "application/json";
    }
  }
  return fetchData(
    `${process.env.REACT_APP_API_URL}/api${path}`,
    finalConfig
  );
};

export const postData = (url, config, body) => {
  return axios.post(url, body, config);
};
